import React from "react";
import { useDispatch } from "react-redux";

import { generatePublicUrl } from "../../urlConfig";
import { openOfficeOrderModal } from "../../actions/officeOrderModal.actions";
import { openOfficeEntityModal } from "../../actions/officeEntityModal.actions";
import { openOfficeProductGallery } from "../../actions/officeProductGallery.actions";
import { openOfficeProduct3D } from "../../actions/officeProduct3D.actions";

const TYPE_META = {
    product: { label: "Товар", icon: "▣" },
    order: { label: "Заказ", icon: "▤" },
    warehouse_material: { label: "Склад", icon: "▦" },
    task: { label: "Задание", icon: "✓" },
    member: { label: "Сотрудник", icon: "●" },
    sales_session: { label: "3D-сессия", icon: "◈" },
};

const TASK_STATUS = {
    published: "Доступно",
    accepted: "Принято",
    in_progress: "В работе",
    review: "На проверке",
    done: "Выполнено",
    rework: "На доработке",
    cancelled: "Отменено",
};

const SESSION_STATUS = {
    active: "Активна",
    closed: "Закрыта",
    converted_to_order: "Создан заказ",
    archived: "Архив",
};

const ROLE_LABELS = {
    owner: "Владелец",
    admin: "Администратор",
    manager: "Менеджер",
    sales: "Продажи",
    installer: "Монтажник",
    driver: "Водитель",
    warehouse: "Склад",
    accountant: "Бухгалтер",
    worker: "Работник",
};

const UNIT_LABELS = {
    pcs: "шт.",
    m: "м",
    m2: "м²",
    m3: "м³",
    kg: "кг",
    t: "т",
    l: "л",
    bag: "меш.",
    bucket: "вед.",
    sheet: "лист",
    set: "компл.",
};

const formatQty = (value, unit) => {
    const number = Number(value || 0);
    const formatted = Number.isInteger(number)
        ? number.toLocaleString("ru-RU")
        : number.toLocaleString("ru-RU", { maximumFractionDigits: 3 });

    return `${formatted} ${UNIT_LABELS[unit] || unit || ""}`.trim();
};

const getImageSrc = (image) => {
    if (!image) return "";
    if (/^(https?:)?\/\//i.test(image) || image.startsWith("data:") || image.startsWith("blob:")) return image;
    if (image.startsWith("/media/") || image.startsWith("/public/")) return image;

    return generatePublicUrl(image);
};

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
};

function EntityFacts({ entity }) {
    const payload = entity?.payload || {};

    if (entity.type === "order") {
        return (
            <>
                {payload.status ? <span>{payload.status}</span> : null}
                {payload.createdAt ? <span>{formatDate(payload.createdAt)}</span> : null}
            </>
        );
    }

    if (entity.type === "product") {
        return (
            <>
                {payload.article !== null && payload.article !== undefined ? <span>Арт. {payload.article}</span> : null}
                {payload.categoryName ? <span>{payload.categoryName}</span> : null}
                {payload.materialName ? <span>{payload.materialName}</span> : null}
                {payload.hasModel ? <span>3D</span> : null}
            </>
        );
    }

    if (entity.type === "warehouse_material") {
        const unit = payload.unit || "pcs";

        return (
            <>
                {payload.sku ? <span>SKU {payload.sku}</span> : null}
                {payload.warehouseName ? <span>{payload.warehouseName}</span> : null}
                {payload.isStockTracked === false ? (
                    <span className="is-stock-untracked">Учёт остатков выключен</span>
                ) : (
                    <>
                        <span>Доступно {formatQty(payload.available, unit)}</span>
                        {Number(payload.reserved || 0) > 0 ? <span>Резерв {formatQty(payload.reserved, unit)}</span> : null}
                        {payload.stockState === "out" ? (
                            <span className="is-stock-out">Нет в наличии</span>
                        ) : payload.stockState === "low" ? (
                            <span className="is-stock-low">Докупить {formatQty(payload.needToBuy, unit)}</span>
                        ) : (
                            <span className="is-stock-ok">Остаток в норме</span>
                        )}
                    </>
                )}
            </>
        );
    }

    if (entity.type === "task") {
        return (
            <>
                {payload.status ? <span>{TASK_STATUS[payload.status] || payload.status}</span> : null}
                {payload.checklistTotal !== undefined ? (
                    <span>Чек-лист {payload.checklistDone || 0}/{payload.checklistTotal || 0}</span>
                ) : null}
                {payload.dueAt ? <span>до {formatDate(payload.dueAt)}</span> : null}
            </>
        );
    }

    if (entity.type === "member") {
        return (
            <>
                {payload.role ? <span>{ROLE_LABELS[payload.role] || payload.role}</span> : null}
                {payload.email ? <span>{payload.email}</span> : null}
            </>
        );
    }

    if (entity.type === "sales_session") {
        return (
            <>
                {payload.sessionId ? <span>{payload.sessionId}</span> : null}
                {payload.status ? <span>{SESSION_STATUS[payload.status] || payload.status}</span> : null}
                {payload.currentTab ? <span>{payload.currentTab}</span> : null}
            </>
        );
    }

    return null;
}

export default function MessageEntityCard({ entity, officeId = null }) {
    const dispatch = useDispatch();

    if (!entity || !entity.type || entity.type === "none") return null;

    const meta = TYPE_META[entity.type] || { label: "Объект", icon: "◆" };
    const imageSrc = getImageSrc(entity.image || entity.payload?.profilePicture);
    const resolvedOfficeId = String(entity.officeId?._id || entity.officeId || officeId || "");
    const entityId = String(entity.id?._id || entity.id || "");

    const handleOpen = () => {
        if (!entityId || !resolvedOfficeId) return;

        if (entity.type === "order") {
            dispatch(openOfficeOrderModal({
                orderId: entityId,
                officeId: resolvedOfficeId,
                activeTab: "overview",
                source: "chat",
                sourceTitle: "Из сообщения чата",
                payload: entity,
            }));
            return;
        }

        dispatch(openOfficeEntityModal({
            type: entity.type,
            entityId,
            officeId: resolvedOfficeId,
            source: "chat",
            sourceTitle: "Из сообщения чата",
            payload: entity,
        }));
    };

    const handleOpenProductGallery = (event) => {
        if (entity.type !== "product" || !entityId || !resolvedOfficeId) return;

        event.preventDefault();
        event.stopPropagation();

        dispatch(openOfficeProductGallery({
            officeId: resolvedOfficeId,
            productId: entityId,
            title: entity.title || "Товар",
            thumbnail: entity.image || "",
            source: "chat-message",
        }));
    };

    const handleOpenProduct3D = (event) => {
        if (
            entity.type !== "product" ||
            entity?.payload?.hasModel !== true ||
            !entityId ||
            !resolvedOfficeId
        ) return;

        event.preventDefault();
        event.stopPropagation();

        dispatch(openOfficeProduct3D({
            officeId: resolvedOfficeId,
            productId: entityId,
            title: entity.title || "Товар",
            source: "chat-message",
            product: entity,
        }));
    };

    const canOpen = !!entityId && !!resolvedOfficeId;
    const canOpenProduct3D = entity.type === "product" && entity?.payload?.hasModel === true && canOpen;

    return (
        <button
            type="button"
            className={`chat-entity-card-AO is-${entity.type}`}
            onClick={handleOpen}
            disabled={!canOpen}
            data-chat-no-select="true"
        >
            <div
                className={[
                    "chat-entity-card-AO__image",
                    entity.type === "product" ? "is-product-gallery" : "",
                ].filter(Boolean).join(" ")}
                onClick={entity.type === "product" ? handleOpenProductGallery : undefined}
                title={entity.type === "product" ? "Открыть фотографии товара" : undefined}
            >
                {imageSrc ? (
                    <img
                        src={imageSrc}
                        alt={entity.title || meta.label}
                        loading="lazy"
                        decoding="async"
                        onError={(event) => {
                            event.currentTarget.style.display = "none";
                        }}
                    />
                ) : (
                    <span className="chat-entity-card-AO__fallback">{meta.icon}</span>
                )}

                {entity.type === "product" ? (
                    <i className="product-gallery-trigger-AO__zoom" aria-hidden="true">⌕</i>
                ) : null}

                {canOpenProduct3D ? (
                    <i
                        className="product-3d-trigger-AO__button"
                        onClick={handleOpenProduct3D}
                        title="Открыть товар в 3D"
                        aria-label="Открыть товар в 3D"
                    >
                        3D
                    </i>
                ) : null}
            </div>

            <div className="chat-entity-card-AO__body">
                <div className="chat-entity-card-AO__head">
                    <div className="chat-entity-card-AO__type">{meta.label}</div>
                    <span className="chat-entity-card-AO__open">Открыть ↗</span>
                </div>

                <div className="chat-entity-card-AO__title">
                    {entity.title || meta.label}
                </div>

                {entity.subtitle ? (
                    <div className="chat-entity-card-AO__subtitle">{entity.subtitle}</div>
                ) : null}

                <div className="chat-entity-card-AO__facts">
                    <EntityFacts entity={entity} />
                </div>
            </div>
        </button>
    );
}
